import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, Type, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TippyDirective } from '@ngneat/helipopper';
import { DropdownBuilderModule } from '@ee/common/dropdown-builder';
import {
  LAYOUT_COLUMNS,
  LEGACY_SPANS,
  LOCK_REASON_TEXT,
  LayoutItem,
  isBlock,
  isLegacyRepresentable,
  resolveMultiSelectOptions,
  spanToPercent
} from '@ee/common/models';
import { AttachmentType, ChoiceLayout, CustomFieldType } from '@ee/common/enums';

/** The field types whose choices a form can narrow. */
const CHOICE_TYPES = new Set<CustomFieldType>([CustomFieldType.SELECT, CustomFieldType.RADIO, CustomFieldType.MULTI_SELECT]);
import { ChoiceListSpec, FieldDefinitionView, LayoutBuilderCapabilities, SIZE_HINT_LABEL } from './intake-layout.model';
import { COMPOSITE_PARTS } from './composites';
import { PLACEMENT_WIDTHS } from './placement-chrome.component';
import { NgComponentOutlet } from '@angular/common';

/**
 * The placement panel — **placement first, definition second**.
 *
 * What you edit here is where a field sits on *this* surface. The definition is shown so you can see
 * what you inherited and jump to it, never so it can be edited in two places.
 *
 * Almost every control carries a line of explanation beneath it. That is not padding: the panel's job
 * is to answer the questions the control itself provokes — what does removing this actually delete,
 * which of the two requiredness gates am I moving — at the moment they are asked.
 */
@Component({
  selector: 'ee-layout-inspector',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DropdownBuilderModule,
    FormsModule,
    MatCheckboxModule,
    MatIconModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    NgComponentOutlet,
    TippyDirective
  ],
  template: `
    <aside class="placement">
      @if (!item) {
        <p class="placement__help">Select a field to edit where it sits and what it is called here.</p>
      } @else {
        <header class="placement__header">
          <span class="layout-chip is-accent">{{ surfaceLabel }} placement</span>
          @if (definition) {
            <button type="button" class="placement__definition" (click)="openDefinition.emit(definition.key)">
              Definition
            </button>
          }
        </header>

        <h3 class="placement__title">{{ headline() }}</h3>
        @if (location) {
          <p class="placement__location">{{ location }}</p>
        }

        @if (item.lock_reason) {
          <p class="placement__lock">
            <mat-icon fontSet="fa" fontIcon="fa-lock" class="layout-icon"></mat-icon>
            <span>{{ lockText() }}</span>
          </p>
        }

        @if (isBlockItem()) {
          @if (item.kind !== 'DIVIDER') {
            <label class="placement__label" [attr.for]="'block-text'">
              {{ item.kind === 'HEADING' ? 'Heading' : 'Rich text' }}
            </label>
            <!--
              A textarea for help text, an input for a heading.

              Rich text is prose and can run to several sentences or several paragraphs; a single-line
              input could hold them but showed forty characters at a time and swallowed the line breaks
              you typed. A heading is one line by definition, so it keeps its input.
            -->
            @if (item.kind === 'RICH_TEXT') {
              <!--
                Quill loads only when a rich-text block is selected. Until it has, the plain field still
                takes the text, so nothing typed in the meantime is lost.
              -->
              @if (richEditor(); as editor) {
                <ng-container *ngComponentOutlet="editor; inputs: { value: item.text, commit: commitText }"></ng-container>
              } @else {
                <textarea id="block-text" class="placement__input placement__input--multiline" rows="4"
                          [ngModel]="item.text" [ngModelOptions]="commitOnBlur"
                          (ngModelChange)="textChange.emit($event)"></textarea>
              }
              <p class="placement__help">Bold, links and lists are kept. Anything a browser could run is removed on save.</p>
            } @else {
              <input id="block-text" class="placement__input" type="text" [ngModel]="item.text"
                     [ngModelOptions]="commitOnBlur"
                     (ngModelChange)="textChange.emit($event)" />
            }
          }
        } @else {
          @if (fixedWidth) {
            <!--
              The section lays its placements out itself (Documents: uploads two to a row). The canvas
              offers no resize handle and the panel has to agree — a width picker whose every value the
              canvas would refuse is a control arguing with itself.
            -->
            <p class="placement__help">This section sets the width (uploads sit two to a row), so there is no width to set.</p>
          } @else if (capabilities.spansAuthorable) {
            <div class="placement__field">
              <div class="placement__label-row">
                <span class="placement__label">Width</span>
                <span class="layout-chip">{{ item.span }}/{{ columns }} · {{ percent(item.span) }}%</span>
              </div>
              <!--
                A segmented picker, not a dropdown: a handful of named widths whose whole point is
                comparison. The same six the field's toolbar offers, plus the current width when a drag
                left it between them; any other span is the resize bar's job.
              -->
              <div class="segmented segmented--wrap" role="group" aria-label="Width">
                @for (span of spanOptions(); track span) {
                  <button type="button" class="segment" [class.is-active]="item.span === span"
                          [attr.aria-pressed]="item.span === span"
                          [disabled]="!editable()"
                          (click)="spanChange.emit(span)">
                    {{ widthLabel(span) }}
                  </button>
                }
              </div>
              <p class="placement__help">
                Drag the field's right edge on the canvas to resize, or pick a width here. To leave a gap before
                it, drop it where it should start (or use alt with the arrow keys).
              </p>
              <!-- Only where the previous layout is still a thing a form could be rolled back to (attorney migration). -->
              @if (capabilities.legacySnap && !legacySafe()) {
                <p class="placement__help placement__help--warn">
                  {{ item.span }}/{{ columns }} has no equivalent in the previous layout, so this section could not be rolled back.
                </p>
              }
            </div>
          } @else {
            <!-- A panel row has one column. The control is not rendered rather than rendered disabled. -->
            <p class="placement__help">This surface has one column, so there is no width to set.</p>
          }

          @if (!choiceList && choiceOptions().length) {
            <!-- Which of the field's choices this form asks; the field keeps them all everywhere else. -->
            <div class="placement__field">
              <div class="placement__label-row">
                <span class="placement__label">Choices shown on this form</span>
                @if (item.visible_options && shownCount() < choiceOptions().length) {
                  <!-- Counted from the field's current choices: a list can name one deleted since. -->
                  <span class="layout-chip">{{ shownCount() }} of {{ choiceOptions().length }}</span>
                }
              </div>
              <div class="placement__choices">
                @for (option of choiceOptions(); track option.value) {
                  <mat-checkbox class="placement__choice" [checked]="isShown(option.value)"
                                [disabled]="!editable() || (isShown(option.value) && shownCount() === 1)"
                                (change)="toggleShown(option.value)">{{ option.label }}</mat-checkbox>
                }
              </div>
              <p class="placement__help">
                Untick a choice to leave it off this form. The case, bulk updates and imports still accept it.
              </p>
            </div>
          }
          @if (hasChoiceLayout()) {
            <div class="placement__field">
              <div class="placement__label-row">
                <span class="placement__label">Show options</span>
                @if (!item.choice_layout) {
                  <span class="layout-chip">Inherited</span>
                }
              </div>
              <div class="segmented" role="group" aria-label="Show options">
                @for (layout of choiceLayouts; track layout.value) {
                  <button type="button" class="segment" [class.is-active]="effectiveChoiceLayout() === layout.value"
                          [attr.aria-pressed]="effectiveChoiceLayout() === layout.value"
                          [disabled]="!editable()"
                          (click)="pickChoiceLayout(layout.value)">
                    {{ layout.label }}
                  </button>
                }
              </div>
              <p class="placement__help">Stacked puts one choice per line; in a row lines them up and wraps.</p>
            </div>
          }
          <div class="placement__field">
            <div class="placement__label-row">
              <!--
                A document's label is its whole identity to the client — three LEASE_AGREEMENT uploads
                are told apart by nothing else — so it is not an override of anything and the panel does
                not offer to reset it to the attachment type's name.
              -->
              <span class="placement__label">{{ isDocument() ? 'Name' : 'Label on this surface' }}</span>
              @if (!isDocument()) {
                <span class="layout-chip">{{ item.label_override ? 'Overridden' : 'Inherited' }}</span>
              }
            </div>
            <input class="placement__input" type="text" [class.is-inherited]="!isDocument() && !item.label_override"
                   [ngModel]="item.label_override ?? (isDocument() ? '' : definition?.label ?? '')"
                   [ngModelOptions]="commitOnBlur" [disabled]="!editable()"
                   (ngModelChange)="labelChange.emit($event)" />
            @if (isDocument()) {
              <p class="placement__help">What the client is asked to upload.</p>
            } @else if (item.label_override && editable()) {
              <button type="button" class="placement__reset" (click)="labelChange.emit(null)">
                Reset to “{{ definition?.label }}”
              </button>
            } @else if (editable()) {
              <p class="placement__help">Inherited — type to override.</p>
            }
          </div>

          @if (isDocument()) {
            <!--
              What v1's Document Editor asked, on the panel rather than behind a modal. The type is the
              attachment it files as; two uploads of the same type are ordinary and are told apart by
              their names.
            -->
            <div class="placement__field">
              <span class="placement__label">Document type</span>
              <select class="placement__input" [ngModel]="item.field_key" [disabled]="!editable()"
                      (ngModelChange)="documentTypeChange.emit($event)">
                @for (type of documentTypes; track type.key) {
                  <option [value]="type.key">{{ type.name }}</option>
                }
              </select>
              <p class="placement__help">Files the upload as this attachment type on the case.</p>
            </div>

          }

          <!--
            The hint and the link, on every question rather than only on uploads.

            v1 kept them in two places a firm could reach — the localization editor, which writes the
            "Hover for more info" text the wizard renders, and the rental-assistance declaration's own
            link field. Both were invisible in v2, so a firm that had explained a claims field watched
            the explanation disappear. They belong on the placement: they explain *this* question here.
          -->
          <div class="placement__field">
            <span class="placement__label">Hint</span>
            <input class="placement__input" type="text" [ngModel]="item.hint ?? definition?.hint ?? ''"
                   [ngModelOptions]="commitOnBlur" [disabled]="!editable()" placeholder="Optional"
                   (ngModelChange)="hintChange.emit($event)" />
            <p class="placement__help">
              {{ isDocument() ? 'The line under the upload, where one document needs explaining.'
                              : 'The line under the control. Shown to the client as it is written.' }}
            </p>
          </div>

          <div class="placement__field">
            <span class="placement__label">More info link</span>
            <input class="placement__input" type="url" [ngModel]="item.more_info_link ?? ''"
                   [ngModelOptions]="commitOnBlur" [disabled]="!editable()" placeholder="Optional"
                   (ngModelChange)="moreInfoLinkChange.emit($event)" />
            <p class="placement__help">An info icon beside the question, opening this in a new tab.</p>
          </div>

          <div class="placement__gate">
            <mat-slide-toggle [ngModel]="item.required_intake === true || item.lock_reason === 'ALWAYS_REQUIRED'"
                              [disabled]="!editable() || item.lock_reason === 'ALWAYS_REQUIRED'"
                              (ngModelChange)="requiredIntakeChange.emit($event)">
              Required to submit
            </mat-slide-toggle>
            <p class="placement__help">Gates the wizard only. The workflow gate lives on the case placement.</p>
          </div>

          @if (showWorkflowGate) {
            <div class="placement__gate">
              <mat-slide-toggle [ngModel]="item.required_workflow === true"
                                [disabled]="!capabilities.canEditLayout"
                                (ngModelChange)="requiredWorkflowChange.emit($event)">
                Required to advance
              </mat-slide-toggle>
              <p class="placement__help">Blocks the step from completing, and shows as “Needed to file” on the case panel.</p>
            </div>
          }
        }

        @if (compositeParts().length) {
          <section class="placement__parts">
            <h4 class="placement__subtitle">Parts</h4>
            <p class="placement__help">
              One question with several parts. It moves and resizes as a unit, and each part keeps its own
              storage key — so documents, the sidebar, imports and the API are unaffected.
            </p>
            <ul class="placement__part-list">
              @for (part of compositeParts(); track part.key) {
                <li>
                  <span>{{ part.label }}</span>
                  @if (capabilities.showAdvanced) {
                    <code>{{ part.key }}</code>
                  }
                  @if (part.derived) {
                    <span class="layout-chip">derived</span>
                  }
                </li>
              }
            </ul>
          </section>
        }

        <!--
          The choices this question offers, where a firm may narrow them.

          Deliberately outside editable(): the eviction reason and the address block are both locked
          placements — they cannot be moved, resized or removed, and the panel says so at the top. That
          lock is about the *structure*. Which choices the list offers is content, and it is a setting
          v1 gave a firm on these very steps; hiding it behind the structural lock would mean the
          migration quietly took it away.
        -->
        @if (choiceList && capabilities.canEditLayout) {
          <section class="placement__field">
            <span class="placement__label">{{ choiceList.label }}</span>
            @if (choiceList.mode === 'FREE') {
              <div class="placement__choices" role="group" [attr.aria-label]="choiceList.label">
                <ee-dropdown-builder [choices]="choices" [fitContent]="true"
                                     (changed)="commitChoices()"></ee-dropdown-builder>
              </div>
            } @else {
              <!--
                Show or hide, never rename or invent: these values mean something past the form, so the
                wording is not a firm's to change. A checkbox each, in the product's order.
              -->
              <div class="placement__choices" role="group" [attr.aria-label]="choiceList.label">
                @for (choice of choiceList.standard; track choice) {
                  <mat-checkbox class="placement__choice" [checked]="offers(choice)"
                                [disabled]="!capabilities.canEditLayout"
                                (change)="toggleChoice(choice, $event.checked)">
                    {{ choice }}
                  </mat-checkbox>
                }
              </div>
            }
            <button type="button" class="placement__reset" [disabled]="isStandardList()"
                    (click)="resetChoices()">
              {{ choiceList.mode === 'FREE' ? 'Reset to the standard list' : 'Offer all of them' }}
            </button>
            <p class="placement__help">
              {{ choiceList.help }}
              @if (isStandardList()) {
                @if (choiceList.mode === 'FREE') {
                  This is the standard list.
                } @else {
                  All of them are offered.
                }
              } @else {
                Reset puts back the standard list and keeps following it if it ever changes.
              }
            </p>
          </section>
        }

        @if (definition?.smart) {
          <p class="placement__help">
            Calculated — shown everywhere, entered nowhere. No editor is created for it on any surface.
          </p>
        }

        @if (canRemove()) {
          <button type="button" class="placement__remove" (click)="remove.emit()">Remove</button>
          <p class="placement__help placement__help--centred">
            Removes the placement only. The definition, the data and every other surface are untouched.
          </p>
        }

        @if (capabilities.showAdvanced && definition) {
          <dl class="placement__dl">
            <dt>Storage key</dt><dd><code>{{ definition.key }}</code></dd>
            <dt>Type</dt><dd>{{ definition.type }}</dd>
            <dt>Default size</dt><dd>{{ sizeHintLabel() }}</dd>
          </dl>
        }
      }
    </aside>
  `,
  styleUrl: './layout-inspector.component.scss'
})
export class LayoutInspectorComponent implements OnChanges, OnInit {
  constructor(private readonly fb: UntypedFormBuilder) {}

  @Input() item: LayoutItem | null = null;

  @Input() definition: FieldDefinitionView | null = null;

  @Input({ required: true }) capabilities!: LayoutBuilderCapabilities;

  /** Which surface this panel is editing — "Intake", "Step", "Panel". */
  @Input() surfaceLabel = 'Intake';

  /** Where the placement sits, e.g. "Page 3 · Claims". */
  @Input() location: string | null = null;

  /** Workflow steps carry the second requiredness gate; intake does not. */
  @Input() showWorkflowGate = false;

  /**
   * The choice list this placement offers, when it has one a firm may narrow.
   *
   * Null for a placement with nothing to narrow, which is most of them. The host works it out — it is
   * the only layer that knows both the declaration's standard list and what the form has stored.
   */
  @Input() choiceList: ChoiceListSpec | null = null;

  @Output() choiceListChange = new EventEmitter<{ optionKey: string; choices: string[] }>();

  /** Whether this placement is a document upload, which the panel edits differently. */
  @Input() isDocumentSection = false;

  /** The section sets every placement's width — see `full_width_only`. No width to choose. */
  @Input() fixedWidth = false;

  @Output() labelChange = new EventEmitter<string | null>();
  @Output() hintChange = new EventEmitter<string>();
  @Output() moreInfoLinkChange = new EventEmitter<string>();
  @Output() documentTypeChange = new EventEmitter<string>();
  @Output() textChange = new EventEmitter<string>();
  @Output() spanChange = new EventEmitter<number>();

  /** The choices this placement shows; null means all of them. */
  @Output() visibleOptionsChange = new EventEmitter<string[] | null>();
  /** This form's stacked-or-in-a-row; null when it follows the field's own. */
  @Output() choiceLayoutChange = new EventEmitter<ChoiceLayout | null>();
  /** A column start for the selected placement, or null for "right after the previous item". */

  @Output() requiredIntakeChange = new EventEmitter<boolean>();
  @Output() requiredWorkflowChange = new EventEmitter<boolean>();
  @Output() remove = new EventEmitter<void>();
  @Output() openDefinition = new EventEmitter<string>();

  /** The rich-text editor, once loaded. Null until a rich-text block is first selected. */
  protected readonly richEditor = signal<Type<unknown> | null>(null);

  /** Passed to the lazily created editor, which cannot bind an output through the outlet. */
  protected readonly commitText = (html: string): void => this.textChange.emit(html);

  private loadRichEditor(): void {
    if (this.richEditor()) return;
    import('./rich-text-block-editor.component').then(m => this.richEditor.set(m.RichTextBlockEditorComponent));
  }

  protected readonly columns = LAYOUT_COLUMNS;

  /**
   * Text edits land when the field is left, not on every keystroke.
   *
   * Each one emits a change, which the host counts, writes into the change log and autosaves — so
   * typing a hint was one save and one "Changed a hint" line per character, and Review changes read as
   * a keystroke log rather than a list of what was done.
   */
  protected readonly commitOnBlur = { updateOn: 'blur' as const };

  /** Every attachment type an upload can file as, in the order the enum declares them. */
  protected readonly documentTypes = Object.entries(AttachmentType).map(([key, name]) => ({ key, name }));

  protected isDocument(): boolean {
    return this.isDocumentSection && !!this.item && !isBlock(this.item);
  }

  protected percent(span: number): number {
    return spanToPercent(span);
  }

  protected isBlockItem(): boolean {
    return !!this.item && isBlock(this.item);
  }

  protected headline(): string {
    if (!this.item) return '';
    if (isBlock(this.item)) {
      return this.item.kind === 'DIVIDER' ? 'Divider' : this.item.kind === 'HEADING' ? 'Heading' : 'Rich text';
    }
    return this.item.label_override || this.definition?.label || this.item.field_key || 'Field';
  }

  protected lockText(): string {
    return this.item?.lock_reason ? LOCK_REASON_TEXT[this.item.lock_reason] : '';
  }

  protected legacySafe(): boolean {
    return !this.item || isLegacyRepresentable(this.item.span);
  }

  /**
   * Legacy-snap narrows the picker to the five spans the previous layout can express, rather than
   * offering eleven and rounding four of them back.
   */
  protected spanOptions(): number[] {
    const named = this.capabilities.legacySnap ? LEGACY_SPANS : PLACEMENT_WIDTHS.map(width => width.span);
    const current = this.item?.span;
    return current && !named.includes(current) ? [...named, current].sort((a, b) => a - b) : [...named];
  }

  /** "1/2", "Full" — the toolbar's words — and "7/12" for a width only the resize bar reaches. */
  protected widthLabel(span: number): string {
    return PLACEMENT_WIDTHS.find(width => width.span === span)?.label ?? `${span}/${LAYOUT_COLUMNS}`;
  }

  /** A choice field's options as value and label, in the field's order; empty for any other field. */
  protected choiceOptions(): { value: string; label: string }[] {
    const definition = this.definition;
    if (!definition || this.item?.kind !== 'FIELD' || !CHOICE_TYPES.has(definition.type)) return [];
    return resolveMultiSelectOptions(definition.field ?? { options: definition.options })
      .map(option => ({ value: option.value, label: option.label || option.value }))
      .filter(option => !!option.value);
  }

  protected readonly choiceLayouts = [
    { value: ChoiceLayout.VERTICAL, label: 'Stacked' },
    { value: ChoiceLayout.HORIZONTAL, label: 'In a row' }
  ];

  /** RADIO and MULTI_SELECT lay their choices out; a SELECT is a dropdown and has nothing to arrange. */
  protected hasChoiceLayout(): boolean {
    const type = this.definition?.type;
    return this.item?.kind === 'FIELD' && (type === CustomFieldType.RADIO || type === CustomFieldType.MULTI_SELECT);
  }

  private fieldChoiceLayout(): ChoiceLayout {
    return this.definition?.field?.choice_layout ?? ChoiceLayout.VERTICAL;
  }

  protected effectiveChoiceLayout(): ChoiceLayout {
    return this.item?.choice_layout ?? this.fieldChoiceLayout();
  }

  /** Picking what the field already does clears the override, so the form follows the field again. */
  protected pickChoiceLayout(layout: ChoiceLayout): void {
    this.choiceLayoutChange.emit(layout === this.fieldChoiceLayout() ? null : layout);
  }

  protected isShown(value: string): boolean {
    return !this.item?.visible_options || this.item.visible_options.includes(value);
  }

  protected shownCount(): number {
    return this.choiceOptions().filter(option => this.isShown(option.value)).length;
  }

  /** Tick or untick one choice. Every choice shown again is `null`, so the form follows the field. */
  protected toggleShown(value: string): void {
    const all = this.choiceOptions().map(option => option.value);
    const shown = new Set(all.filter(option => this.isShown(option)));
    if (shown.has(value)) shown.delete(value);
    else shown.add(value);
    if (!shown.size) return;
    this.visibleOptionsChange.emit(shown.size === all.length ? null : all.filter(option => shown.has(option)));
  }

  protected sizeHintLabel(): string {
    return this.definition ? SIZE_HINT_LABEL[this.definition.size_hint] : '';
  }

  protected compositeParts(): { key: string; label: string; derived?: boolean }[] {
    const type = this.item?.composite_type ?? this.definition?.composite;
    return type ? COMPOSITE_PARTS[type] : [];
  }

  /**
   * The editable list behind the choices editor.
   *
   * Seeded from the firm's list, falling back to the standard one, because an empty stored value means
   * "offer all of them" — showing an empty editor for it would look like the setting was lost.
   */
  protected readonly choices: UntypedFormArray = this.fb.array([]);

  /** True while the firm has not narrowed or extended the standard list. */
  /** Whether a fixed-value choice is currently offered. Nothing chosen means all of them are. */
  protected offers(choice: string): boolean {
    const current = this.choiceList?.current ?? [];
    return !current.length || current.includes(choice);
  }

  /**
   * Show or hide one fixed value.
   *
   * Emitted in the product's order rather than the order they were ticked: the wording is not the
   * firm's to change and neither is the sequence — a client should see the reasons in the order the
   * product lists them however the boxes were clicked.
   */
  protected toggleChoice(choice: string, checked: boolean): void {
    const spec = this.choiceList;
    if (!spec) return;
    const current = spec.current.length ? spec.current : [...spec.standard];
    const next = checked ? [...current, choice] : current.filter(value => value !== choice);
    const ordered = spec.standard.filter(value => next.includes(value));
    this.choiceListChange.emit({
      optionKey: spec.optionKey,
      // Every one of them ticked is how "not narrowed" is stored, so it keeps following the product.
      choices: ordered.length === spec.standard.length ? [] : ordered
    });
  }

  protected isStandardList(): boolean {
    return !this.choiceList?.current.length;
  }

  /** Push the edited list up. An emptied list is the same as the default: the client is offered all. */
  protected commitChoices(): void {
    if (!this.choiceList) return;
    const next = (this.choices.value as string[])
      .map(value => (value ?? '').trim())
      .filter(value => value.length > 0);
    this.choiceListChange.emit({
      optionKey: this.choiceList.optionKey,
      choices: this.matchesStandardList(next) ? [] : next
    });
  }

  /**
   * Put back the standard list.
   *
   * Emits empty rather than a copy of it on purpose. Empty is how "offer all of them" has always been
   * stored, and it keeps following the standard list — a firm that resets today still picks up a
   * choice added to the product tomorrow, which a frozen copy would not.
   */
  protected resetChoices(): void {
    if (!this.choiceList) return;
    this.choiceListChange.emit({ optionKey: this.choiceList.optionKey, choices: [] });
    this.seedChoices(this.choiceList.standard);
  }

  /** Whether a list is the standard one, same members in the same order. */
  private matchesStandardList(list: string[]): boolean {
    const standard = this.choiceList?.standard ?? [];
    return list.length === standard.length && list.every((value, index) => value === standard[index]);
  }

  private seedChoices(values: readonly string[]): void {
    this.choices.clear({ emitEvent: false });
    values.forEach(value =>
      this.choices.push(this.fb.control(value, [Validators.required]), { emitEvent: false })
    );
  }

  /**
   * Bring the editor in line with {@link choiceList}.
   *
   * Driven from both lifecycle hooks on purpose. `ngOnChanges` alone misses the case where the input
   * is never reassigned — a placement that has narrowed nothing, or a host that binds it once — and
   * the editor would sit empty. It is a no-op when the list already matches, which matters because
   * every edit round-trips out through the parent and back: rebuilding the array on the way back
   * would drop the row the firm is still editing.
   */
  private syncChoices(): void {
    const spec = this.choiceList;
    const incoming = !spec ? [] : spec.current.length ? spec.current : spec.standard;
    const current = this.choices.value as string[];
    const unchanged =
      current.length === incoming.length && current.every((value, i) => value === incoming[i]);
    if (!unchanged) {
      this.seedChoices(incoming);
    }
  }

  ngOnInit(): void {
    this.syncChoices();
    this.loadEditorsFor();
  }

  ngOnChanges(): void {
    this.syncChoices();
    this.loadEditorsFor();
  }

  private loadEditorsFor(): void {
    if (this.item?.kind === 'RICH_TEXT') {
      this.loadRichEditor();
    }
  }

  /**
   * Whether this placement's properties can be changed at all.
   *
   * A locked placement is the product's: it says so at the top of the panel, and then every control
   * beneath it has to agree. A live width picker and an editable label under "Needed to create the
   * client's account, so it stays in this section" is a panel arguing with itself — and the edits it
   * accepts are ones the canvas will refuse.
   */
  protected editable(): boolean {
    return this.capabilities.canEditLayout && !this.item?.lock_reason;
  }

  protected canRemove(): boolean {
    if (!this.item || !this.capabilities.canEditLayout) {
      return false;
    }
    return !this.item.lock_reason;
  }
}
